import type { MisconceptionDefinition } from './types'

/**
 * STU — Studievaardigheden misconcepties.
 *
 * STATUS (mei 2026): **Phase A — pragmatische registratie**.
 *
 * Deze file registreert de misconception-tag-IDs zoals ze momenteel in de
 * 8 getagde STU-seeds voorkomen. Het primaire doel is:
 *
 *   ✅  Het `PEDAGOGY_MISCONCEPTION_UNKNOWN` (severity 70) signaal in de
 *       pipeline-ruis-baseline elimineren.
 *
 * Een deel van de IDs hieronder schendt `MISCONCEPTION_REGISTRY_SSOT_2026.md
 * §3 regel 2` (extra dots binnen de ID-body, bv. `stu.alfabet.b_before_a`
 * heeft 2 dots). Dat is een **bewust uitgesteld** consequentiekader:
 *
 *   • Canonicaliseren naar `stu.alfabet-b-voor-a` vereist tegelijk de
 *     8 seed-bestanden hernoemen → grotere PR, content-review nodig.
 *   • Eerst het ruis-issue dempen, dan in een aparte sweep de IDs naar
 *     kebab-case zonder extra dots brengen.
 *
 * Phase B (geplanned, P3): rename-pass over STU-seeds → kebab-canonical IDs
 * → deze file bijwerken. Zie `PEDAGOGY_MISCONCEPTIONS_SYNC_AUDIT_2026.md §6.3`
 * en `SYSTEM_INTEGRATION_AUDIT_2026_2.md §10.4`.
 */
export const STU_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ───────────────────────────────────────────────────────────
  // STU-ALFABET — alfabetische volgorde
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.alfabet.b_before_a',
    label: 'Denkt dat B vóór A komt',
    module: 'Studievaardig',
    description:
      'Leerling beheerst de alfabetvolgorde nog niet en plaatst B vóór A, of kiest een random volgorde.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet.subjective_order',
    label: 'Subjectieve volgorde i.p.v. alfabet',
    module: 'Studievaardig',
    description:
      'Leerling rangschikt woorden op persoonlijke voorkeur (favoriet, eerste-gezien) i.p.v. de alfabetische conventie.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet.no_order_needed',
    label: 'Volgorde optioneel geacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat alfabetisch ordenen optioneel is en plaatst woorden willekeurig.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet.too_broad',
    label: 'Range te breed gekozen',
    module: 'Studievaardig',
    description:
      'Leerling kiest een te brede letter-range (bv. A–M i.p.v. C–E) en mist daardoor precisie.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet.skip_second_letter',
    label: 'Tweede letter niet gebruikt',
    module: 'Studievaardig',
    description:
      'Bij woorden met dezelfde beginletter kijkt leerling niet door naar de tweede of derde letter om de volgorde te bepalen.',
    typicalFamilies: ['STU-ALFABET', 'STU-WOORDENBOEK'],
  },
  {
    id: 'stu.alfabet.wrong_letter',
    label: 'Woord onder verkeerde beginletter',
    module: 'Studievaardig',
    description:
      'Leerling plaatst een woord onder een letter die niet de beginletter is (bv. door verwarring met klank vs. spelling).',
    typicalFamilies: ['STU-ALFABET', 'STU-WOORDENBOEK'],
  },
  {
    id: 'stu.alfabet.z_special',
    label: 'Z als speciale positie',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat Z (of een andere randletter) een uitzonderlijke positie heeft binnen het alfabet.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet.wrong_letter_pos',
    label: 'Letter bij verkeerde middenletter',
    module: 'Studievaardig',
    description:
      'Leerling weet de globale richting (begin/eind) maar plaatst de letter bij de verkeerde middenletter.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet.order_optional',
    label: 'Positie binnen alfabet doet er niet toe',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de exacte positie binnen het alfabet niet uitmaakt voor ordening of opzoeken.',
    typicalFamilies: ['STU-ALFABET'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-COMBINEER — meerdere bronnen combineren
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.zoek-op-opdracht',
    label: 'Zoekt op de opdracht i.p.v. het onderwerp',
    module: 'Studievaardig',
    description:
      'Leerling typt de letterlijke vraag in een zoekmachine i.p.v. een kernwoord uit het onderwerp.',
    typicalFamilies: ['STU-COMBINEER', 'STU-INTERNET'],
  },
  {
    id: 'stu.zoekterm-te-breed',
    label: 'Zoekterm te breed',
    module: 'Studievaardig',
    description:
      'Leerling gebruikt één algemeen woord (bv. "dieren") i.p.v. een specifieke samengestelde zoekterm.',
    typicalFamilies: ['STU-COMBINEER', 'STU-INTERNET'],
  },
  {
    id: 'stu.weer-vs-klimaat',
    label: 'Weer en klimaat verward',
    module: 'Studievaardig',
    description:
      'Leerling gebruikt "weer" (dag-fluctuatie) en "klimaat" (gemiddelde over jaren) door elkaar bij het selecteren van bronnen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.conclusie-buiten-bron',
    label: 'Conclusie buiten bron',
    module: 'Studievaardig',
    description:
      'Leerling trekt een conclusie die niet door de gegeven bronnen wordt ondersteund (verzint of voegt eigen kennis toe).',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.bron-negeren',
    label: 'Bron genegeerd',
    module: 'Studievaardig',
    description:
      'Leerling negeert relevante cijfers of citaten in de aangeboden bronnen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.bronnen-vergelijken-i.p.v.-combineren',
    label: 'Bronnen vergeleken i.p.v. gecombineerd',
    module: 'Studievaardig',
    description:
      'Leerling zet bronnen naast elkaar (vergelijken) i.p.v. ze samen te gebruiken om één conclusie te bouwen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.een-bron-volstaat',
    label: 'Eén bron volstaat',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat één bron genoeg is voor een onderbouwde conclusie, terwijl de opdracht expliciet om combineren vraagt.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.bron-doelgroep-verwart',
    label: 'Doelgroep van bron verward',
    module: 'Studievaardig',
    description:
      'Leerling past informatie uit een bron toe op de verkeerde doelgroep (bv. volwassen-richtlijnen op kinderen).',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.anekdote-als-bewijs',
    label: 'Anekdote als bewijs',
    module: 'Studievaardig',
    description:
      'Leerling beschouwt persoonlijke verhalen of vrienden-uitspraken als gelijkwaardig bewijs aan systematische bronnen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.commercieel-als-onafhankelijk',
    label: 'Commerciële bron als onafhankelijk',
    module: 'Studievaardig',
    description:
      'Leerling onderscheidt commerciële (reclame, fabrikant) en onafhankelijke bronnen niet.',
    typicalFamilies: ['STU-COMBINEER', 'STU-INTERNET'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-DIAGRAM — diagram-keuze en -lezen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.diagram.fraction_third',
    label: '¼ en ⅓ verward',
    module: 'Studievaardig',
    description:
      'Leerling herkent het verschil tussen een kwart-segment en een derde-segment in een cirkeldiagram niet visueel.',
    typicalFamilies: ['STU-DIAGRAM', 'STU-CIRKEL'],
  },
  {
    id: 'stu.diagram.percent_as_count',
    label: 'Percentage als aantal gelezen',
    module: 'Studievaardig',
    description:
      'Leerling leest een percentage in het diagram als absoluut aantal i.p.v. relatief deel.',
    typicalFamilies: ['STU-DIAGRAM', 'STU-CIRKEL'],
  },
  {
    id: 'stu.diagram.fraction_half',
    label: '¼ en ½ verward',
    module: 'Studievaardig',
    description: 'Leerling verwart een kwart met een half in cirkeldiagrammen.',
    typicalFamilies: ['STU-DIAGRAM', 'STU-CIRKEL'],
  },
  {
    id: 'stu.diagram.pick_circle_for_time',
    label: 'Cirkeldiagram voor tijdverloop',
    module: 'Studievaardig',
    description:
      'Leerling kiest een cirkeldiagram (deel-van-geheel) waar een lijndiagram (verloop) passender is.',
    typicalFamilies: ['STU-DIAGRAM'],
  },
  {
    id: 'stu.diagram.pick_bar_for_trend',
    label: 'Staafdiagram voor trend',
    module: 'Studievaardig',
    description:
      'Leerling kiest een staafdiagram (categorisch) waar een lijndiagram (trend) passender is.',
    typicalFamilies: ['STU-DIAGRAM'],
  },
  {
    id: 'stu.diagram.pick_pictogram_for_continuous',
    label: 'Pictogram voor continue data',
    module: 'Studievaardig',
    description:
      'Leerling kiest een pictogram (discrete categorieën) voor continue meetgegevens.',
    typicalFamilies: ['STU-DIAGRAM'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-FLOWCHART — procesflowchart lezen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.flowchart.shape_confusion_action',
    label: 'Ruit en rechthoek verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een ruit (beslissing) met een rechthoek (actie).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.shape_confusion_start',
    label: 'Ruit en begin verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een ruit (beslissing) met een ovaal (start).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.shape_confusion_end',
    label: 'Ruit en einde verward',
    module: 'Studievaardig',
    description: 'Leerling verwart een ruit (beslissing) met een ovaal (eind).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.both_paths',
    label: 'Beide paden tegelijk gevolgd',
    module: 'Studievaardig',
    description:
      'Leerling volgt beide ja/nee-paden uit een beslissingsruit i.p.v. te kiezen.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.implicit_loop',
    label: 'Niet-bestaande loop verondersteld',
    module: 'Studievaardig',
    description:
      'Leerling veronderstelt dat het proces terugkeert terwijl er geen terugpijl in de flowchart staat.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.decision_is_end',
    label: 'Beslissingsruit gezien als einde',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat het proces stopt bij een beslissingsruit i.p.v. dat er verder wordt gegaan langs ja/nee-pijl.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.purpose_bar',
    label: 'Flowchart en staafdiagram verward',
    module: 'Studievaardig',
    description:
      'Leerling kent de procesfunctie van een flowchart niet en verwart het met een staafdiagram.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.purpose_pie',
    label: 'Flowchart en cirkeldiagram verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een flowchart met een cirkeldiagram (deel-van-geheel).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.purpose_line',
    label: 'Flowchart en lijndiagram verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een flowchart met een lijndiagram (verloop over tijd).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.loop_is_error',
    label: 'Terugpijl gezien als fout',
    module: 'Studievaardig',
    description:
      'Leerling interpreteert een terugpijl (loop) als tekening-fout i.p.v. bewuste herhaling.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.loop_restart_all',
    label: 'Loop herstart hele proces',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een terugpijl het hele proces opnieuw start i.p.v. alleen de loop-stap herhaalt.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart.loop_is_end',
    label: 'Loop als einde gezien',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een terugpijl markeert dat het proces eindigt.',
    typicalFamilies: ['STU-FLOWCHART'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-INHOUDSOPGAVE — inhoudsopgave gebruiken
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.inhoud.confuse_glossary',
    label: 'Inhoudsopgave en woordenlijst verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een inhoudsopgave (hoofdstukken) met een woordenlijst (begrippen).',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.confuse_author',
    label: 'Auteursinfo in inhoudsopgave verwacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat schrijver- of uitgever-informatie in de inhoudsopgave staat.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.confuse_summary',
    label: 'Inhoudsopgave en flaptekst verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een inhoudsopgave (structuur) met een flaptekst (samenvatting).',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.wrong_chapter',
    label: 'Verkeerd hoofdstuk gekozen',
    module: 'Studievaardig',
    description:
      'Leerling kiest het paginanummer van een ander hoofdstuk dan gevraagd.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.wrong_chapter_2',
    label: 'Verkeerd hoofdstuk gekozen (variant)',
    module: 'Studievaardig',
    description:
      'Tweede variant van "verkeerd hoofdstuk": leerling kiest aangrenzend hoofdstuk i.p.v. het gevraagde.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.invent_page',
    label: 'Paginanummer verzonnen',
    module: 'Studievaardig',
    description:
      'Leerling verzint een paginanummer i.p.v. de inhoudsopgave te raadplegen.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.start_as_count',
    label: 'Startpagina als hoofdstuklengte',
    module: 'Studievaardig',
    description:
      'Leerling geeft de startpagina van een hoofdstuk als antwoord op "hoe lang is dit hoofdstuk?".',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.next_start_as_count',
    label: 'Volgende startpagina als hoofdstuklengte',
    module: 'Studievaardig',
    description:
      'Leerling geeft de startpagina van het volgende hoofdstuk i.p.v. te subtraheren om de lengte te krijgen.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.off_by_one',
    label: 'Off-by-one bij pagina-telling',
    module: 'Studievaardig',
    description:
      'Leerling maakt een 1-pagina afwijking bij het berekenen van hoofdstuklengte uit startpaginas.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.confuse_with_index',
    label: 'Inhoudsopgave en register verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart de inhoudsopgave (voorin, hoofdstuk-volgorde) met het register (achterin, alfabetisch).',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.wrong_loc_middle',
    label: 'Inhoudsopgave in midden verwacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de inhoudsopgave in het midden van het boek staat i.p.v. voorin.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.confuse_blurb',
    label: 'Inhoudsopgave en flaptekst verward',
    module: 'Studievaardig',
    description:
      'Leerling kijkt op de flaptekst om inhoudsopgave-informatie te vinden.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.read_all',
    label: 'Hele boek doorlezen',
    module: 'Studievaardig',
    description:
      'Leerling leest het hele boek door om een hoofdstuk te vinden i.p.v. de inhoudsopgave te gebruiken.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.random_search',
    label: 'Willekeurig bladeren',
    module: 'Studievaardig',
    description:
      'Leerling bladert willekeurig door het boek i.p.v. de inhoudsopgave te gebruiken als index.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud.backflap_first',
    label: 'Flaptekst als eerste oriëntatie',
    module: 'Studievaardig',
    description:
      'Leerling begint bij de flaptekst voor structuur-info terwijl de inhoudsopgave die rol vervult.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-MINDMAP — mindmap-structuur
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.mindmap.center_details',
    label: 'Details in het midden',
    module: 'Studievaardig',
    description:
      'Leerling plaatst details in het centrum i.p.v. het hoofdonderwerp.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.center_conclusion',
    label: 'Conclusie in het midden',
    module: 'Studievaardig',
    description:
      'Leerling verwart mindmap-centrum met de conclusie van een samenvatting.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.center_sources',
    label: 'Bronnenlijst in het midden',
    module: 'Studievaardig',
    description:
      'Leerling verwart het centrum van een mindmap met een bronnen- of literatuurlijst.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.color_decoration',
    label: 'Kleur als decoratie gezien',
    module: 'Studievaardig',
    description:
      'Leerling ziet kleuren in een mindmap als versiering i.p.v. als informatie-grouperende code.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.color_required',
    label: 'Kleur verplicht geacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een mindmap niet correct is zonder kleur, terwijl kleur optioneel-functioneel is.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.color_more_text',
    label: 'Kleur verward met tekstcapaciteit',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een gekleurde tak meer tekst kan dragen dan een ongekleurde.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.full_sentences',
    label: 'Hele zinnen op takken',
    module: 'Studievaardig',
    description:
      'Leerling schrijft hele zinnen op mindmap-takken i.p.v. kernwoorden.',
    typicalFamilies: ['STU-MINDMAP', 'STU-NOTITIES'],
  },
  {
    id: 'stu.mindmap.page_numbers',
    label: 'Mindmap met paginanummers',
    module: 'Studievaardig',
    description:
      'Leerling verwart mindmap (begrip-relaties) met inhoudsopgave (paginanummers).',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.questions_branches',
    label: 'Vragen als takken',
    module: 'Studievaardig',
    description:
      'Leerling verwart mindmap-structuur met een vragenstrategie (KWL — Know/Want/Learned).',
    typicalFamilies: ['STU-MINDMAP'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-NOTITIES — notities maken
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.notities.verbatim',
    label: 'Letterlijk overschrijven',
    module: 'Studievaardig',
    description:
      'Leerling probeert alles letterlijk op te schrijven i.p.v. te parafraseren tot kernwoorden.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.beauty',
    label: 'Functie en esthetiek verward',
    module: 'Studievaardig',
    description:
      'Leerling besteedt energie aan mooie notities i.p.v. aan informatie-dichtheid en herbruikbaarheid.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.show_attention',
    label: 'Notities als sociaal signaal',
    module: 'Studievaardig',
    description:
      'Leerling maakt notities om aandachtig over te komen i.p.v. om later te kunnen leren.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.full_sentences',
    label: 'Hele zinnen in notities',
    module: 'Studievaardig',
    description:
      'Leerling schrijft hele zinnen in notities i.p.v. compacte kernwoorden.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.title_only',
    label: 'Alleen titels genoteerd',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de hoofdstuk- of paragraaftitel volstaat als notitie.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.one_long_sentence',
    label: 'Eén lange zin',
    module: 'Studievaardig',
    description:
      'Leerling schrijft notities als één lange, ongestructureerde zin.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.wait_test',
    label: 'Pas voor toets bekijken',
    module: 'Studievaardig',
    description:
      'Leerling wacht tot vlak voor de toets om notities terug te lezen, mist daardoor spaced-repetition effect.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.never_review',
    label: 'Nooit teruglezen',
    module: 'Studievaardig',
    description:
      'Leerling bekijkt notities na het maken nooit meer; notities als wegwerp-handeling.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities.only_when_asked',
    label: 'Alleen op verzoek leraar',
    module: 'Studievaardig',
    description:
      'Leerling raadpleegt notities alleen als de leraar daarom vraagt, geen eigen leer-strategie.',
    typicalFamilies: ['STU-NOTITIES'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-SAMENVATTEN — samenvattingen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.samenvatten.write_first',
    label: 'Schrijven zonder lezen',
    module: 'Studievaardig',
    description:
      'Leerling begint te schrijven zonder eerst de tekst overzichtelijk te lezen of te scannen.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.only_intro',
    label: 'Alleen eerste alinea dekt alles',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de eerste alinea altijd de hele tekst samenvat.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.vocab_first',
    label: 'Woordstudie i.p.v. leestechniek',
    module: 'Studievaardig',
    description:
      'Leerling verwart samenvatten met begrippen-opzoeken; behandelt het als woordstudie.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.copy_verbatim',
    label: 'Samenvatten = overschrijven',
    module: 'Studievaardig',
    description:
      'Leerling verwart samenvatten met letterlijk overschrijven van zinnen.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.title_only',
    label: 'Titel als samenvatting',
    module: 'Studievaardig',
    description: 'Leerling denkt dat de titel een volledige samenvatting is.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.paraphrase_all',
    label: 'Alles parafraseren (te lang)',
    module: 'Studievaardig',
    description:
      'Leerling parafraseert elke zin afzonderlijk en eindigt met een samenvatting die langer is dan het origineel.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.omit_main_idea',
    label: 'Hoofdgedachte weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling laat de hoofdgedachte weg in een samenvatting, focust op secundaire details.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.omit_key_points',
    label: 'Belangrijke punten weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling laat belangrijke ondersteunende punten weg en houdt alleen anekdotes over.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten.omit_conclusion',
    label: 'Conclusie weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling laat de conclusie/synthese weg uit een samenvatting.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
]
